import React from "react";
import { formatDay } from "../api/shape.js";
import { NOT_ATTENDED_MAX } from "../api/endOfDay.js";
import { PhoneFullScreen, PhoneSheet } from "./PhoneShell.jsx";

/**
 * End of day on a phone (mockups/cockpit-v3/mobile/EndOfDay.dc.html). The same
 * model and the same state (useEndOfDay) as the desktop screen; a full-screen
 * view with the one submit in its footer.
 */

const EOD_FOOTER =
  "Approving is your sign-off for the client: the weekly report counts approved records only. Untick a record to leave it open; the inspector can still change it. A record with a defect noted becomes a repair check for that block, item and side.";

function toneClass(tone) {
  return tone === "ok" ? "ok" : tone === "warn" ? "warn" : tone === "bad" ? "bad" : "";
}

function SideCard({ side, state, captureUrlFor }) {
  const busy = state.pendingKey === side.key;
  return (
    <div className="ph-dy-card" data-testid="eod-side" data-state="to_go" data-side-ref={side.scheduleRef}>
      <div className="ph-dy-top">
        <span className="ph-dy-time">{side.timeText}</span>
        <span className="ph-dy-chip bad">NO RESULT</span>
      </div>
      <span className="ph-dy-item">{side.title}</span>
      <span className="ph-dy-place">{[side.place, side.who].filter(Boolean).join(" · ")}</span>
      <button type="button" className="ph-dy-act" disabled={busy || state.submitting} onClick={() => state.carry(side)}>{busy ? "…" : "Carry to tomorrow"}</button>
      <div className="ph-dy-two">
        <a className="ph-dy-act" href={captureUrlFor(side.projectCode)} target="_blank" rel="noreferrer">Record now</a>
        <button type="button" className="ph-dy-act" disabled={busy || state.submitting} onClick={() => state.openReason(side)}>Not attended…</button>
      </div>
    </div>
  );
}

function PostponedCard({ side, model, state }) {
  const keep = state.kept.has(side.key);
  return (
    <div className="ph-dy-card" data-testid="eod-postponed" data-keep={keep ? "true" : "false"} data-side-ref={side.sides.map((x) => x.scheduleRef).join(" ")}>
      <div className="ph-dy-top">
        <span className="ph-dy-time">{side.timeText}</span>
        <span className={`ph-dy-chip ${keep ? "" : "warn"}`}>{keep ? "KEPT ON TODAY" : model.carryLabel.toUpperCase()}</span>
      </div>
      <span className="ph-dy-item">{side.title}</span>
      <span className="ph-dy-place">{["Postponed by the yard", side.who].join(" · ")}</span>
      <button type="button" className="ph-dy-act" onClick={() => state.toggleKeep(side.key)}>{keep ? `Carry to ${model.carryLabel}` : "Keep on today"}</button>
    </div>
  );
}

function ClosedCard({ side, model }) {
  const text = side.state === "carried" ? `Carried to ${side.carriedTo ? formatDay(`${side.carriedTo}T00:00:00Z`) : model.carryLabel}` : `Not attended: ${side.notAttendedReason}`;
  return (
    <div className="ph-dy-card" data-testid="eod-closed" data-state={side.state}>
      <div className="ph-dy-top">
        <span className="ph-dy-time">{side.timeText}</span>
        <span className="ph-dy-chip">{side.state === "carried" ? "CARRIED" : "NOT ATTENDED"}</span>
      </div>
      <span className="ph-dy-item">{side.title}</span>
      <span className="ph-dy-place">{text}</span>
    </div>
  );
}

function RecordRow({ rec, state }) {
  const ticked = !state.unticked.has(rec.key);
  return (
    <label className="ph-dy-row ph-dy-eod-rec" data-testid="eod-record" data-ticked={ticked ? "true" : "false"} data-inspection={rec.inspectionNumber}>
      <span className="ph-dy-eod-line">
        <input type="checkbox" checked={ticked} onChange={() => state.toggleRecord(rec.key)} aria-label={`Approve ${rec.title} ${rec.sideText}`} />
        <span className="ph-dy-time">{rec.time}</span>
        <span className={`ph-dy-chip ${toneClass(rec.resultTone)}`}>{rec.result}</span>
      </span>
      <span className="ph-dy-item">{rec.title} · {rec.sideText}</span>
      <span className="ph-dy-place">
        {rec.defectNoted ? <span className="ph-dy-defect">Defect noted</span> : "No defect"} · {rec.photoCount === 0 ? "No photos" : `${rec.photoCount} ${rec.photoCount === 1 ? "photo" : "photos"}`}{rec.capturedBy ? ` · ${rec.capturedBy}` : ""}
      </span>
    </label>
  );
}

function ReasonSheet({ side, state }) {
  return (
    <PhoneSheet labelledBy="ph-eod-reason-title" onClose={state.closeReason} testId="ph-eod-reason">
      <h2 id="ph-eod-reason-title" className="ph-sheet-title">Not attended</h2>
      <p className="ph-dy-muted">{side.title}</p>
      <label className="ph-dy-field">
        Why was this not attended? (1 to {NOT_ATTENDED_MAX} characters)
        <textarea className="ph-dy-eod-input" rows={3} data-autofocus value={state.reasonText} onChange={(e) => state.setReasonText(e.target.value)} />
      </label>
      {state.reasonError ? <span role="alert" className="ph-dy-defect" data-testid="eod-reason-error">{state.reasonError}</span> : null}
      <button type="button" className="ph-btn pri" disabled={state.pendingKey === side.key} onClick={() => state.confirmReason(side)}>Mark not attended</button>
      <button type="button" className="ph-btn" onClick={state.closeReason}>Cancel</button>
    </PhoneSheet>
  );
}

export default function PhoneEndOfDay({ model, errors = [], state, onBack, captureUrlFor }) {
  const s = model.summary;
  const reasonSide = state.reasonKey ? model.noResult.find((x) => x.key === state.reasonKey) : null;
  return (
    <>
    <PhoneFullScreen
      title="End of day"
      subtitle={model.dateLabel}
      titleId="ph-eod-title"
      backLabel="Back to Today"
      onBack={onBack}
      testId="phone-end-of-day"
      footer={
        model.available ? (
          <button type="button" className="ph-btn pri" data-testid="eod-approve" disabled={!state.canSubmit} onClick={state.submit}>{state.submitting ? "Approving…" : state.submitLabel}</button>
        ) : null
      }
    >
      <div className="ph-dy-eod-body" data-day-date={model.date}>
        {errors.length > 0 ? (
          <section className="ph-section" data-testid="eod-errors">
            {errors.map((e) => (
              <div className="ph-card" key={e.code}><span className="ph-cardsub" style={{ color: "var(--ph-bad)" }}>{e.code}: this day is unavailable &mdash; {e.error}</span></div>
            ))}
          </section>
        ) : null}
        {state.message ? (
          <div role="alert" className={`ph-card ${state.message.tone === "ok" ? "" : "warnbox"}`} data-testid="eod-message" data-tone={state.message.tone}>{state.message.text}</div>
        ) : null}

        {!model.available ? (
          <section className="ph-dy-empty" data-testid="eod-unavailable">
            <h2>This day is not available</h2>
            <p>The day could not be read. Go back to Today and try again.</p>
          </section>
        ) : (
          <>
            <section className="ph-dy-summary" aria-label="Summary of the day" data-testid="eod-summary">
              <div className="ph-dy-figures">
                <span className="req">{s.recordedText}</span>
              </div>
              {s.postponedText ? <span className="ph-dy-muted" style={{ color: "var(--ph-warn)" }}>{s.postponedText}</span> : null}
              {s.unscheduledText ? <span className="ph-dy-muted">{s.unscheduledText}</span> : null}
              {s.noResultText ? <span className="ph-dy-muted" style={{ color: "var(--ph-bad)" }}>{s.noResultText}</span> : null}
              {s.closedText ? <span className="ph-dy-muted">{s.closedText}</span> : null}
            </section>

            {model.noResult.length > 0 ? (
              <section className="ph-section" style={{ gap: 10 }} data-testid="eod-section-noresult">
                <h2 className="ph-dy-who" style={{ color: "var(--ph-bad)" }}>Requested by the yard, no result recorded · {model.noResult.length}</h2>
                {model.noResult.map((side) => <SideCard key={side.key} side={side} state={state} captureUrlFor={captureUrlFor} />)}
              </section>
            ) : null}

            {model.postponed.length > 0 ? (
              <section className="ph-section" style={{ gap: 10 }} data-testid="eod-section-postponed">
                <h2 className="ph-dy-who warn">Postponed · carried to {model.carryLabel}</h2>
                {model.postponed.map((side) => <PostponedCard key={side.key} side={side} model={model} state={state} />)}
              </section>
            ) : null}

            {model.closed.length > 0 ? (
              <section className="ph-section" style={{ gap: 10 }} data-testid="eod-section-closed">
                <h2 className="ph-dy-who">Already closed · {model.closed.length}</h2>
                {model.closed.map((side) => <ClosedCard key={side.key} side={side} model={model} />)}
              </section>
            ) : null}

            <section className="ph-section" style={{ gap: 10 }} data-testid="eod-section-records">
              <h2 className="ph-dy-who" style={{ color: "var(--ph-t1)" }}>Records to approve · {model.records.length}</h2>
              {model.records.length > 0 ? (
                <>
                  <label className="ph-dy-link ph-dy-eod-all">
                    <input type="checkbox" checked={state.allTicked} onChange={state.toggleAll} aria-label="Select all records" />
                    <span className="ph-dy-muted">{model.withDefects} with defects · {model.withoutPhotos} without photos{model.withoutRequest ? ` · ${model.withoutRequest} without a yard request` : ""}</span>
                  </label>
                  <div className="ph-dy-rows">
                    {model.records.map((rec) => <RecordRow key={rec.key} rec={rec} state={state} />)}
                  </div>
                </>
              ) : (
                <div className="ph-dy-link"><span className="ph-dy-muted" data-testid="eod-no-records">{model.approvedCount ? `All ${model.approvedCount} records of this day are approved.` : "No records on this day."}</span></div>
              )}
              <p className="ph-dy-note" data-testid="eod-footer">{EOD_FOOTER}</p>
            </section>
          </>
        )}
      </div>
    </PhoneFullScreen>
    {reasonSide ? <ReasonSheet side={reasonSide} state={state} /> : null}
    </>
  );
}
